import { useCallback, useEffect, useRef, useState } from 'react'
import { authRequest } from '../util/api/auth-api'
import useStore from '../util/store/auth'
import getUser from '../util/auth/getUser'
import EmptyState from '../components/EmptyState'
import { Outlet } from 'react-router-dom'
import EmptyPageContainer from '../components/EmptyPageContainer'
import useRequireAuth from '../util/auth/useRequireAuth'

/**
 * RequireDirectoryAdmin is a wrapper component that ensures:
 * The user has administrator or master counselor access to the directory.
 */
const RequireDirectoryAdmin = () => {
  const [hasDirectoryAdminAccess, setHasDirectoryAdminAccess] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const { directory } = useStore()
  const currentlyCheckingDir = useRef<string | null>(null)
  const user = getUser()
  const currentPage = globalThis.location.pathname + globalThis.location.search

  useRequireAuth(currentPage)

  const handleDirectoryAdminAccess = useCallback(async () => {
    try {
      if (!user || !directory) return

      setIsLoading(true)
      currentlyCheckingDir.current = directory._id

      const { results } = await authRequest.user.directories.roles.list({
        token: '',
        directoryId: directory._id,
        search: {
          and: [{ userId: user._id }],
          or: [{ permission: 'administrator' }, { permission: 'master-counselor' }],
        },
      })

      // Check if the user has admin access to the organization
      const hasAccess = results.some(
        (r) =>
          (r.permission === 'administrator' || r.permission === 'master-counselor') &&
          r.userId === user._id
      )

      if (currentlyCheckingDir.current !== directory._id) return

      setHasDirectoryAdminAccess(hasAccess)
    } catch (error) {
      console.error('Error checking directory admin access', error)
      setHasDirectoryAdminAccess(false)
    } finally {
      setIsLoading(false)
    }
  }, [directory, user])

  useEffect(() => {
    void handleDirectoryAdminAccess()
  }, [handleDirectoryAdminAccess])

  if (!user) {
    return null
  }

  if (isLoading) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="loading"
          title="Loading..."
          description="Loading the organization details"
        />
      </EmptyPageContainer>
    )
  }

  // Allow system admins to access the internal pages regardless of permissions
  if (user?.admin) {
    return <Outlet />
  }

  if (!hasDirectoryAdminAccess) {
    return (
      <EmptyPageContainer>
        <EmptyState
          emptyStateVariant="error"
          title="You are not authorized to access this page"
          description="Please contact your organization admin"
        />
      </EmptyPageContainer>
    )
  }

  return <Outlet />
}

export default RequireDirectoryAdmin
